import React, { useState, useEffect, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../api/treeTrackerApi';
import FilterListIcon from '@material-ui/icons/FilterList';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import styles from './Home.styles';
import { documentTitle } from '../../common/variables';
import Menu from '../common/Menu';
import { AppContext } from '../../context/AppContext';
import {
  hasFreetownPermission,
  hasPermission,
  POLICIES,
} from '../../models/auth';
import {
  DashStatGrowerCount,
  DashStatTotalCaptures,
  DashStatUnprocessedCaptures,
  DashStatVerifiedCaptures,
} from '../DashStat.container';
import GreenStandSvgLogo from '../images/GreenStandSvgLogo';
import ReportingCard1 from '../reportingCards/ReportingCard1';
import ReportingCard2 from '../reportingCards/ReportingCard2';
import ReportingCard3 from '../reportingCards/ReportingCard3';
import ReportingCard4 from '../reportingCards/ReportingCard4';
import ReportingCard5 from '../reportingCards/ReportingCard5';
import ReportingCard6 from '../reportingCards/ReportingCard6';
import ReportingCard7 from '../reportingCards/ReportingCard7';
import ReportingCard8 from '../reportingCards/ReportingCard8';
import ReportingCard9 from '../reportingCards/ReportingCard9';
import ReportingCard10 from '../reportingCards/ReportingCard10';
//import ReportingCard11 from '../reportingCards/ReportingCard11';
import ReportingCard12 from '../reportingCards/ReportingCard12';
import MenuItem from '@material-ui/core/MenuItem';
import MenuMui from '@material-ui/core/Menu';
import { format, subDays, formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import log from 'loglevel';
import { getMyBinding } from '../../api/organizationBinding';
import { getOrganizationById } from '../../api/organizations';

/**
 * @function
 * @name Home
 * @description renders the home page
 * @param {object} props
 * @param {object} props.classes css classes
 *
 * @returns {React.Component} Home component
 */
function Home(props) {
  const { classes } = props;
  const appContext = useContext(AppContext);
  const organizationId = appContext.user?.policy?.organization?.id;
  const { data: binding } = useQuery({
    queryKey: ['organizationBinding'],
    queryFn: getMyBinding,
    enabled: !appContext.userHasOrg,
  });
  const { data: organization } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => getOrganizationById(organizationId),
    enabled: Boolean(organizationId),
  });
  const isBindingPending = binding?.status === 'pending';
  // A user who already belongs or is waiting has nothing to apply for.
  const showOrganizationApplyLink = !appContext.userHasOrg && !isBindingPending;
  const [anchorEl, setAnchorEl] = useState(null);
  const [updateTime, setUpdateTime] = useState(undefined);

  useEffect(() => {
    document.title = `${documentTitle}`;
  }, []);

  // the reporting card, update time, the time range
  useEffect(() => {
    async function loadUpdateTime() {
      const res = await axios(
        `${process.env.REACT_APP_REPORTING_API_ROOT}/capture/statistics?`
      );
      const { data } = res;
      setUpdateTime(data.last_updated_at);
    }
    loadUpdateTime();
  }, []);
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ['species'],
      queryFn: () => api.getSpecies(),
      staleTime: 1000 * 60 * 5, // cache for 5 mins
      refetchOnWindowFocus: false,
    });
  }, [queryClient]);

  const timeRange = [
    { range: 30, text: 'Last Month' },
    { range: 30 * 6, text: 'Last 6 Months' },
    { range: 365, text: 'Last Year' },
    { range: 365 * 100, text: 'All' },
  ];
  const [timeRangeIndex, setTimeRangeIndex] = useState(3);
  const [startDate, setStartDate] = useState('1970-01-01');
  const [endDate /*, setEndDate*/] = useState(format(new Date(), 'yyyy-MM-dd'));

  const handleTimeClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTimeClose = (index) => {
    log.warn('index: ', index);
    setAnchorEl(null);
    if (isNaN(index)) return;
    setTimeRangeIndex(index);
    setStartDate(format(subDays(new Date(), timeRange[0].range), 'yyyy-MM-dd'));
  };

  return (
    <Grid className={classes.box}>
      <Grid className={classes.menuAside}>
        <Paper elevation={3} className={classes.menu}>
          <Menu variant="plain" />
        </Paper>
      </Grid>
      <Grid className={classes.rightBox}>
        <Box className={classes.box2}>
          <Grid container spacing={5} className={classes.version}>
            <Grid item>
              <GreenStandSvgLogo />
              <Box display="inline" ml={2}>
                Version: {`${process.env.REACT_APP_VERSION}`}
              </Box>
              {showOrganizationApplyLink ? (
                <>
                  <Box className={classes.orgApplicationLinkRow}>
                    <Typography
                      variant="body2"
                      className={classes.orgApplicationText}
                    >
                      <Link
                        component={RouterLink}
                        to="/organization/apply"
                        className={classes.orgApplicationLink}
                      >
                        Apply to become a Greenstand associated organization
                      </Link>
                    </Typography>
                  </Box>
                  <Box className={classes.orgApplicationLinkRow}>
                    <Typography
                      variant="body2"
                      className={classes.orgApplicationText}
                    >
                      <Link
                        component={RouterLink}
                        to="/organization/bind"
                        className={classes.orgApplicationLink}
                        data-testid="home-bind-organization"
                      >
                        Request to bind to an existing organization
                      </Link>
                    </Typography>
                  </Box>
                </>
              ) : null}
              {isBindingPending ? (
                <Box className={classes.orgApplicationLinkRow}>
                  <Typography
                    variant="body2"
                    className={classes.orgApplicationText}
                    data-testid="home-binding-pending"
                  >
                    You are waiting for approval of organization binding
                  </Typography>
                </Box>
              ) : null}
              {organization ? (
                <Box className={classes.orgApplicationLinkRow}>
                  <Typography
                    variant="body2"
                    className={classes.orgApplicationText}
                    data-testid="home-organization-member"
                  >
                    {`You are the member of organization: ${organization.name}`}
                  </Typography>
                </Box>
              ) : null}
            </Grid>
            {hasFreetownPermission(appContext.user) && (
              <Grid item xs={5} className={classes.timeBox}>
                {updateTime && (
                  <Typography variant="body1" className={classes.time}>
                    Last updated{' '}
                    {formatDistanceToNow(new Date(updateTime), {
                      addSuffix: true,
                    })}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  onClick={handleTimeClick}
                  className={classes.timeButton}
                >
                  <FilterListIcon color="primary" />
                  <Typography variant="body1">
                    {timeRange[timeRangeIndex].text}
                  </Typography>
                </Button>
                <MenuMui
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleTimeClose}
                  classes={{ paper: classes.timeMenu }}
                >
                  {timeRange.map((item, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => handleTimeClose(index)}
                    >
                      {timeRange[index].text}
                    </MenuItem>
                  ))}
                </MenuMui>
              </Grid>
            )}
          </Grid>
          <div className={classes.dashstatWraper}>
            {hasPermission(appContext.user, [
              POLICIES.SUPER_PERMISSION,
              POLICIES.LIST_TREE,
              POLICIES.APPROVE_TREE,
            ]) &&
              !hasFreetownPermission(appContext.user) && (
                <>
                  <DashStatTotalCaptures />
                  <DashStatUnprocessedCaptures />
                  <DashStatVerifiedCaptures />
                </>
              )}
            {hasPermission(appContext.user, [
              POLICIES.SUPER_PERMISSION,
              POLICIES.LIST_PLANTER,
            ]) &&
              !hasFreetownPermission(appContext.user) && (
                <DashStatGrowerCount />
              )}
            {hasFreetownPermission(appContext.user) && (
              <Grid item xs={12} className={classes.statCardGrid} container>
                <Grid item xs={4}>
                  <ReportingCard1 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard2 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard9 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard3 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard4 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard5 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard6 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard7 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard8 startDate={startDate} endDate={endDate} />
                </Grid>
                <Grid item xs={4}>
                  <ReportingCard10 startDate={startDate} endDate={endDate} />
                </Grid>
                {/*
                <Grid item xs={4}>
                  <ReportingCard11 startDate={startDate} endDate={endDate} />
                </Grid>
                */}
                <Grid item xs={4}>
                  <ReportingCard12 startDate={startDate} endDate={endDate} />
                </Grid>
              </Grid>
            )}
          </div>
        </Box>
      </Grid>
    </Grid>
  );
}

export default withStyles(styles)(Home);
